/**
 * Snakes and Ladders Tokens Component
 *
 * Renders player tokens at their board positions.
 * Supports multiple tokens on the same square (offset display).
 * Animates token movement.
 */

import React from 'react';
import { PlayerState, BOARD_SIZE } from '../types';
import { getBoardPosition } from './SnakesLaddersBoard';
import './SnakesLaddersTokens.css';

interface SnakesLaddersTokensProps {
  players: PlayerState[];
  currentPlayerIndex: number;
  animating?: boolean;
}

const TOKEN_COLORS: Record<string, string> = {
  red: '#e53935',
  blue: '#1e88e5',
  green: '#43a047',
  yellow: '#fdd835',
};

const TOKEN_EMOJIS: Record<string, string> = {
  red: '🔴',
  blue: '🔵',
  green: '🟢',
  yellow: '🟡',
};

/** Get the pixel offset for multiple tokens on the same square */
function getTokenOffset(index: number, total: number): { dx: number; dy: number } {
  if (total <= 1) return { dx: 0, dy: 0 };
  const offsets = [
    { dx: -25, dy: -25 },
    { dx: 25, dy: -25 },
    { dx: -25, dy: 25 },
    { dx: 25, dy: 25 },
  ];
  return offsets[index] || { dx: 0, dy: 0 };
}

const SnakesLaddersTokens: React.FC<SnakesLaddersTokensProps> = ({
  players,
  currentPlayerIndex,
  animating,
}) => {
  // Group players by position for offset calculation
  const positionGroups = new Map<number, PlayerState[]>();
  for (const player of players) {
    if (player.position === 0) continue; // Off board
    const group = positionGroups.get(player.position) || [];
    group.push(player);
    positionGroups.set(player.position, group);
  }

  return (
    <div className="sl-tokens" aria-label="Player tokens">
      {players.map((player, playerIdx) => {
        if (player.position === 0) {
          // Show token in the "start" area
          return (
            <div
              key={player.id}
              className={`sl-token sl-token--start sl-token--${player.color} ${playerIdx === currentPlayerIndex ? 'sl-token--current' : ''}`}
              aria-label={`${player.color} token at start`}
              style={{
                '--token-color': TOKEN_COLORS[player.color],
              } as React.CSSProperties}
            >
              <span className="sl-token__emoji" aria-hidden="true">
                {TOKEN_EMOJIS[player.color]}
              </span>
            </div>
          );
        }

        const pos = getBoardPosition(player.position);
        const group = positionGroups.get(player.position) || [player];
        const indexInGroup = group.indexOf(player);
        const offset = getTokenOffset(indexInGroup, group.length);

        // Calculate CSS position as percentage
        const cellSize = 100 / BOARD_SIZE;
        const left = pos.col * cellSize + cellSize / 2;
        const top = (BOARD_SIZE - 1 - pos.row) * cellSize + cellSize / 2;

        return (
          <div
            key={player.id}
            className={`sl-token sl-token--${player.color} ${playerIdx === currentPlayerIndex ? 'sl-token--current' : ''} ${animating ? 'sl-token--animating' : ''}`}
            style={{
              left: `calc(${left}% + ${offset.dx}%)`,
              top: `calc(${top}% + ${offset.dy}%)`,
              '--token-color': TOKEN_COLORS[player.color],
            } as React.CSSProperties}
            aria-label={`${player.color} token on square ${player.position}`}
          >
            <span className="sl-token__emoji" aria-hidden="true">
              {TOKEN_EMOJIS[player.color]}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export { SnakesLaddersTokens };
export default SnakesLaddersTokens;
